import { useEffect, useMemo, useState } from 'react';

import { CustomerSupportIcon, HeadsetIcon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { AdminPermissions } from 'constants/admin-permissions';
import { Currencies } from 'constants/currencies';
import { MESSAGE_STATE_ERROR } from 'constants/message-app-state-contants';
import { setAdminRoute } from 'helpers/session';
import patternValidators from 'helpers/validators/pattern-validators';
import { useAccessControl } from 'hooks/access-control';
import { useDocumentTitle } from 'hooks/document-title';
import AdminPage from 'layouts/AdminPage/AdminPage';
import moment from 'moment';
import { Tab, Tabs } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { RequestAccessModal } from './components/RequestAccessModal/RequestAccessModal';
import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import {
  Button,
  DatePicker,
  FormMessage,
  Input,
  InputGroup,
  Select,
  ToggleSwitch,
} from 'components/FormComponents';
import { StatusPill } from 'components/Pills/StatusPills';
import { RowDetails } from 'components/RowDetails/RowDetails';

import { messagePopUp } from 'api/app-slice';
import { loginAs } from 'api/authentication-slice';
import { selectAuthentication } from 'api/authentication-slice';
import { useGetCheckTypesQuery } from 'api/check-types-api';
import {
  useGetCompanyBillingQuery,
  useGetCompanyChecksQuery,
  useGetCompanyOwnerQuery,
  useGetCompanyQuery,
  useGetCompanyTeamsQuery,
  useGetCompanyUsersQuery,
  useLazyGetCompanyBillingReportQuery,
  useLazyGetCompanyUsageReportQuery,
  useUpdateCompanyBillingMutation,
  useUpdateCompanyMutation,
} from 'api/company-api';
import {
  useCreateCompanyCheckMutation,
  useDeleteCompanyCheckMutation,
  useUpdateCompanyCheckMutation,
} from 'api/company-checks-api';
import { useCreatePurchaseMutation } from 'api/purchases-api';
import {
  useCreateSupportAccessRequestMutation,
  useGetCompanySupportAccessRequestsQuery,
} from 'api/support-access-api';

import styles from './AdminCompany.module.scss';
import { ChecksTab } from './tabs/ChecksTab/ChecksTab';

const AdminCompany = () => {
  let { company_id: companyId } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authentication = useSelector(selectAuthentication);

  const { data: companyData, refetch: refetchCompany } = useGetCompanyQuery(companyId, {
    refetchOnMountOrArgChange: true,
  });
  const { data: user } = useGetCompanyOwnerQuery(companyId);
  const { data: usersData } = useGetCompanyUsersQuery({ id: companyId });
  const { data: checkTypes } = useGetCheckTypesQuery();
  const { data: billing, refetch: refetchBilling } = useGetCompanyBillingQuery(companyId);
  const {
    data: companyChecksData,
    isLoading: loadingCompanyChecks,
    refetch: refetchCompanyChecks,
  } = useGetCompanyChecksQuery(companyId, {
    refetchOnMountOrArgChange: true,
  });

  const { data: companyTeamsData } = useGetCompanyTeamsQuery(companyId, {
    skip: !companyId,
    refetchOnMountOrArgChange: true,
  });

  const { data: companySupportRequests, refetch: refetchSupportRequests } =
    useGetCompanySupportAccessRequestsQuery(companyId, {
      skip: !companyId || companyData?.support_access_level !== 'request',
    });

  const [getCompanyUsageReport] = useLazyGetCompanyUsageReportQuery();
  const [getCompanyBillingReport] = useLazyGetCompanyBillingReportQuery();
  const [updateCompanyCheck] = useUpdateCompanyCheckMutation();
  const [deleteCompanyCheck] = useDeleteCompanyCheckMutation();
  const [createCompanyCheck] = useCreateCompanyCheckMutation();
  const [companyUpdate] = useUpdateCompanyMutation();
  const [updateCompanyBilling] = useUpdateCompanyBillingMutation();
  const [createPurchase] = useCreatePurchaseMutation();
  const [createSupportAccessRequest, { isLoading: requestingAccess }] =
    useCreateSupportAccessRequestMutation();

  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState();
  const [companyChecks, setCompanyChecks] = useState();
  const [users, setUsers] = useState([]);

  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [showBillingReportModal, setShowBillingReportModal] = useState(false);
  const [showRequestAccessModal, setShowRequestAccessModal] = useState(false);

  useDocumentTitle(company ? company.name : 'Loading...');

  const hasPermission = useAccessControl(authentication.admin_permissions);

  const {
    watch,
    register,
    reset,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    disabled: !hasPermission(AdminPermissions.UpdateCompany),
  });

  useEffect(() => {
    if (!companyData) return;

    setCompany(companyData);
    reset(companyData);

    return () => {
      setCompany(null);
      setCompanyChecks([]);
    };
  }, [companyData]);

  watch('name');
  const isPaused = watch('pause');

  useEffect(() => {
    if (companyChecksData) setCompanyChecks(companyChecksData);
  }, [companyChecksData]);

  useEffect(() => {
    if (!billing) return;
    if (billing.billing_currency) setValue('billing_currency', billing.billing_currency);
    setValue('include_pricing_in_billing', !!billing.include_pricing_in_billing);
  }, [billing]);
  useEffect(() => {
    if (usersData) setUsers(usersData.result.users);
  }, [usersData]);

  const companyForChecksTab = useMemo(() => {
    if (!company) return null;
    const teams = companyTeamsData?.map(team => ({ value: team.id, name: team.name }));
    if (!teams) return company;
    return { ...company, teams };
  }, [company, companyTeamsData]);

  const handleAddCredits = async data => {
    const result = await createPurchase(data);
    if (result.error) {
      dispatch(
        messagePopUp({ text: result.error.data.error.log, state: MESSAGE_STATE_ERROR, hide: true }),
      );
      return result;
    }
    setCompanyChecks(current =>
      current?.map(check =>
        check.type === data.check_type ? { ...check, credits: result.data } : check,
      ),
    );
    return result;
  };

  const onUpdate = async data => {
    setLoading(true);
    const { billing_currency, include_pricing_in_billing, ...companyData } = data;
    await companyUpdate({ id: companyId, data: companyData });
    if (billing && billing_currency) {
      await updateCompanyBilling({
        id: companyId,
        data: { billing_currency, include_pricing_in_billing: !!include_pricing_in_billing },
      });
      refetchBilling();
    }
    refetchCompany();
    refetchCompanyChecks();
    setLoading(false);
  };

  const handleCompanyCheckUpdate = async (id, data = {}) => {
    setLoading(true);
    const { data: updatedCompanyCheck, error } = await updateCompanyCheck({ id, data });
    if (error) {
      dispatch(
        messagePopUp({ text: error.data.error.log, state: MESSAGE_STATE_ERROR, hide: true }),
      );
      setLoading(false);
      return false;
    }
    setCompanyChecks(
      companyChecks.map(companyCheck =>
        companyCheck.id === updatedCompanyCheck.id ? updatedCompanyCheck : companyCheck,
      ),
    );
    setLoading(false);
  };

  const toggleCompanyCheck = async (id, type, value) => {
    setLoading(true);
    if (value) {
      const { data: companyCheck, error } = await createCompanyCheck({
        company_id: companyId,
        type,
      });
      if (error) {
        dispatch(
          messagePopUp({ text: error.data.error.log, state: MESSAGE_STATE_ERROR, hide: true }),
        );
        setLoading(false);
        return false;
      }
      setCompanyChecks([...companyChecks, companyCheck]);
    } else {
      const { error } = await deleteCompanyCheck(id);
      if (error) {
        dispatch(
          messagePopUp({ text: error.data.error.log, state: MESSAGE_STATE_ERROR, hide: true }),
        );
        setLoading(false);
        return false;
      }
      setCompanyChecks(curr => curr.filter(companyCheck => companyCheck.id !== id));
    }
    setLoading(false);
    return true;
  };

  const handleLoginAs = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      await dispatch(loginAs(user.id)).unwrap();
      setAdminRoute(`/admin/companies/${companyId}`);
      window.location.href = '/dashboard';
    } catch {
      dispatch(
        messagePopUp({
          text: 'Could not log in as this user. The company may not allow support access.',
          state: MESSAGE_STATE_ERROR,
          hide: true,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadUsageReport = async filters => {
    setLoading(true);
    await getCompanyUsageReport({ id: companyId, data: filters });
    setLoading(false);
  };

  const downloadBillingReport = async filters => {
    setLoading(true);
    await getCompanyBillingReport({
      id: companyId,
      data: {
        date_from: filters.date_from,
        date_to: filters.date_to,
        replace: isWholeBillingMonth(filters.date_from, filters.date_to) && filters.replace ? 1 : 0,
      },
    });
    setLoading(false);
    setShowBillingReportModal(false);
  };

  const handleRequestAccess = async data => {
    setLoading(true);
    const result = await createSupportAccessRequest({ ...data, company_id: companyId });
    if (result.error) {
      dispatch(
        messagePopUp({
          text: result.error.data?.error?.log,
          state: MESSAGE_STATE_ERROR,
          hide: true,
        }),
      );
      setLoading(false);
      return;
    }
    setLoading(false);
    setShowRequestAccessModal(false);
    refetchSupportRequests();
  };

  const renderLoginAsAction = () => {
    if (company?.support_access_level === 'no_access') {
      return (
        <StatusPill
          status="error"
          iconColor="#c32d2d"
          icon={CustomerSupportIcon}
          text="No Support Access"
          className="u-margin-right--small"
        />
      );
    }

    if (company?.support_access_level === 'request') {
      const currentRequest = companySupportRequests?.find(
        r => r.status === 'pending' || r.status === 'active',
      );

      if (currentRequest?.status === 'active') {
        const expired =
          currentRequest.expires_at && new Date(currentRequest.expires_at) <= new Date();
        if (!expired) {
          return (
            <RowDetails.Action
              icon="key"
              tooltip="Login into company as admin"
              onClick={handleLoginAs}
            />
          );
        }
      }

      const isPending = currentRequest?.status === 'pending';

      return (
        <RowDetails.Action
          icon={HeadsetIcon}
          prefix="hugeicons"
          disabled={isPending}
          tooltip={
            isPending
              ? 'Access request already sent. Waiting for customer approval.'
              : 'Request support access'
          }
          onClick={() => setShowRequestAccessModal(true)}
        />
      );
    }

    return (
      <RowDetails.Action icon="key" tooltip="Login into company as admin" onClick={handleLoginAs} />
    );
  };

  const renderStatusBadge = () => {
    if (company?.support_access_level !== 'request') return null;

    const currentRequest = companySupportRequests?.find(
      r => r.status === 'pending' || r.status === 'active',
    );
    if (!currentRequest) return null;

    if (currentRequest.status === 'pending') {
      return (
        <StatusPill
          status="warning"
          text="Pending"
          iconColor="text-[#554800]"
          icon={CustomerSupportIcon}
          className="u-margin-right--small u-pointer !bg-surface-status-pending-20 !border-surface-status-pending !text-[#554800]"
          onClick={() => navigate(`/admin/support_access`)}
        />
      );
    }

    const expired = currentRequest.expires_at && new Date(currentRequest.expires_at) <= new Date();
    if (expired) return null;

    return (
      <StatusPill
        status="success"
        text="Active"
        icon={CustomerSupportIcon}
        tooltip={
          currentRequest.expires_at
            ? 'Expires ' + moment(currentRequest.expires_at).format('D MMM YYYY, h:mma')
            : undefined
        }
        className="u-margin-right--small"
      />
    );
  };

  return (
    <AdminPage
      loading={loading || loadingCompanyChecks}
      title={company?.name}
      allowedRole={AdminPermissions.ListCompanies}>
      <ReportFiltersModal
        visible={showFiltersModal}
        onSubmit={downloadUsageReport}
        onClose={() => setShowFiltersModal(false)}
      />
      <BillingReportModal
        visible={showBillingReportModal}
        onSubmit={downloadBillingReport}
        onClose={() => setShowBillingReportModal(false)}
      />
      <RequestAccessModal
        visible={showRequestAccessModal}
        loading={requestingAccess}
        onSubmit={handleRequestAccess}
        onClose={() => setShowRequestAccessModal(false)}
      />

      {company && !company.active && (
        <div className="surface-white u-padding-top u-padding-x">
          <FormMessage
            type="Info"
            closeable={false}
            noMargin
            message="This company account is inactive. No candidates have been created in the past 18 months."
          />
        </div>
      )}
      {company?.pause && (
        <div className="surface-white u-padding-top u-padding-x">
          <FormMessage
            type="Warning"
            closeable={false}
            noMargin
            message={
              'This company is paused.' +
              (company.pause_reason ? ' Reason: ' + company.pause_reason : '')
            }
          />
        </div>
      )}
      <RowDetails className={styles.details}>
        <RowDetails.Column title="ID" data={company?.id} />
        <RowDetails.Column title="Name" data={company?.name} />
        <RowDetails.Column title="Owner Name" data={user?.name} />
        <RowDetails.Column title="Owner Email" data={user?.email} />
        {hasPermission(AdminPermissions.LoginAsClient) && renderLoginAsAction()}
        {hasPermission(AdminPermissions.ListCompanies) && (
          <RowDetails.Action
            icon="file-text"
            tooltip="Download company usage report"
            onClick={() => setShowFiltersModal(true)}
          />
        )}
        {hasPermission(AdminPermissions.ListCompanies) && (
          <RowDetails.Action
            icon="download"
            tooltip="Download billing report"
            onClick={() => setShowBillingReportModal(true)}
          />
        )}
        {hasPermission(AdminPermissions.LoginAsClient) && renderStatusBadge()}
      </RowDetails>

      <Tabs defaultActiveKey="config" className="tabs no-padding padding-small">
        <Tab eventKey="config" title="Configuration" className="u-padding--large">
          <form
            className="card card-with-border u-padding u-width-25"
            onSubmit={handleSubmit(onUpdate)}>
            <ToggleSwitch
              register={register}
              name="checkmate_branding_disabled"
              label="Deactivate Checkmate branding"
            />
            <ToggleSwitch register={register} name="audit_logs_enabled" label="Audit Logs" />
            <ToggleSwitch
              register={register}
              name="audit_logs_mask_names"
              label="Hide candidate names in Audit Logs"
            />
            <ToggleSwitch
              register={register}
              name="renewal_dashboard_enabled"
              label="Renewal Dashboard"
            />
            <ToggleSwitch register={register} name="ff_ai_forms" label="AI Forms FF" />
            <ToggleSwitch register={register} name="ff_translations" label="Translations FF" />
            <ToggleSwitch
              register={register}
              name="ff_enable_legacy_report"
              label="Enable Legacy Report FF"
            />
            <ToggleSwitch
              register={register}
              name="ff_reporting_billing_filter"
              label="Reporting Billing Filter FF"
            />
            <ToggleSwitch register={register} name="use_volunteers" label="Use Volunteers" />
            <ToggleSwitch
              register={register}
              name="allow_portal_access"
              label="Allow Portal Access"
              tooltip="When off, the portal will be disabled for the candidates of this company."
            />
            <ToggleSwitch
              register={register}
              name="show_support_widget"
              label="Show Support Widget"
              tooltip="When off, the Intercom support widget will be hidden for this company."
            />
            <InputGroup title="Account type" className="u-width-100">
              <Select
                name="account_type"
                register={register}
                value={watch('account_type')}
                useDefault>
                <Select.Item value="simple">Simple</Select.Item>
                <Select.Item value="enterprise">Enterprise</Select.Item>
                <Select.Item value="internal">Internal</Select.Item>
                <Select.Item value="demo">Demo</Select.Item>
                <Select.Item value="sandbox">Sandbox</Select.Item>
              </Select>
            </InputGroup>
            {billing && (
              <>
                <InputGroup title="Billing Currency" className="u-width-100">
                  <Select
                    name="billing_currency"
                    register={register}
                    value={watch('billing_currency')}
                    useDefault>
                    {Object.keys(Currencies).map(currency => (
                      <Select.Item key={currency} value={currency}>
                        {currency.toUpperCase()}
                      </Select.Item>
                    ))}
                  </Select>
                </InputGroup>
                <ToggleSwitch
                  register={register}
                  name="include_pricing_in_billing"
                  label="Billing Report - Include Price"
                  inputProps={{
                    checked: !!watch('include_pricing_in_billing'),
                    onChange: event => setValue('include_pricing_in_billing', event.target.checked),
                  }}
                />
              </>
            )}
            <InputGroup title="Account Manager" className="u-width-100">
              <Select
                name="account_manager"
                register={register}
                value={watch('account_manager')}
                useDefault>
                {process.env.REACT_APP_ACCOUNT_MANAGERS.split(',').map(account_manager => (
                  <Select.Item
                    key={account_manager}
                    className="u-capitalize"
                    value={account_manager}>
                    {account_manager}
                  </Select.Item>
                ))}
                <Select.Item value="other">Other</Select.Item>
              </Select>
            </InputGroup>
            <InputGroup title="Support contact email">
              <Input
                name="contact_email"
                placeholder="support@checkmate.tech"
                register={register}
                validators={patternValidators.email}
                error={errors?.contact_email?.message}
              />
            </InputGroup>
            <InputGroup title="Checkmate Entity" className="u-width-100">
              <Select
                name="checkmate_entity"
                register={register}
                value={watch('checkmate_entity')}
                useDefault>
                <Select.Item value="checkmate_technology_limited">
                  Checkmate Technology Limited - NZ
                </Select.Item>
                <Select.Item value="checkmate_technology_pty_ltd">
                  Checkmate Technology Pty Ltd - AU
                </Select.Item>
                <Select.Item value="checkmate_inc">Checkmate, Inc. - US</Select.Item>
              </Select>
            </InputGroup>
            <InputGroup title="Support Access Level" className="u-width-100">
              <Select
                name="support_access_level"
                register={register}
                value={watch('support_access_level')}
                useDefault>
                <Select.Item value="open" description="Support can access anytime.">
                  Open Access
                </Select.Item>
                <Select.Item value="request" description="Client approval required.">
                  Request Permission
                </Select.Item>
                <Select.Item value="no_access" description="Support cannot access.">
                  No Support Access
                </Select.Item>
              </Select>
            </InputGroup>
            <ToggleSwitch
              register={register}
              name="pause"
              label="Pause Company"
              tooltip="When on, the company cannot launch new checks or add credits."
            />
            {isPaused && (
              <InputGroup title="Pause Reason">
                <Input
                  name="pause_reason"
                  placeholder="Reason for pausing this company"
                  register={register}
                />
              </InputGroup>
            )}

            {hasPermission(AdminPermissions.UpdateCompany) && (
              <Button className="u-width-100 u-margin-top--large" submit>
                Save
              </Button>
            )}
          </form>
        </Tab>
        <Tab eventKey="checks" title="Checks" className="u-padding--large">
          <ChecksTab
            company={companyForChecksTab ?? company}
            users={users}
            checkTypes={checkTypes}
            companyChecks={companyChecks}
            billingCurrency={billing?.billing_currency}
            disableUpdates={!hasPermission(AdminPermissions.UpdateCompany)}
            setLoading={setLoading}
            onToggle={toggleCompanyCheck}
            onSave={handleCompanyCheckUpdate}
            onAddCredits={handleAddCredits}
          />
        </Tab>
      </Tabs>
    </AdminPage>
  );
};

export default AdminCompany;

const ReportFiltersModal = ({ visible, onSubmit = () => {}, onClose = () => {} }) => {
  const { control, handleSubmit } = useForm();

  return (
    <AnimatedModal title="Filters" visible={visible} onClose={onClose}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={cn('u-margin-top', styles.filtersModalForm)}>
        <InputGroup title="Date From">
          <Controller
            control={control}
            name="date_from"
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <DatePicker value={value} onChange={onChange} outputFormat="YYYY-MM-DD" />
            )}
          />
        </InputGroup>
        <InputGroup title="Date To">
          <Controller
            control={control}
            name="date_to"
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <DatePicker value={value} onChange={onChange} outputFormat="YYYY-MM-DD" />
            )}
          />
        </InputGroup>
        <Button submit className="u-width-100 u-margin-top--large">
          Download
        </Button>
      </form>
    </AnimatedModal>
  );
};

const previousBillingMonth = () => {
  const month = moment().subtract(1, 'month');
  return {
    date_from: month.clone().startOf('month').format('YYYY-MM-DD'),
    date_to: month.clone().endOf('month').format('YYYY-MM-DD'),
  };
};

const isWholeBillingMonth = (dateFrom, dateTo) => {
  const from = moment(dateFrom, 'YYYY-MM-DD', true);
  const to = moment(dateTo, 'YYYY-MM-DD', true);
  if (!from.isValid() || !to.isValid() || !from.isSame(to, 'month')) return false;
  return (
    from.isSame(from.clone().startOf('month'), 'day') && to.isSame(to.clone().endOf('month'), 'day')
  );
};

const BillingReportModal = ({ visible, onSubmit = () => {}, onClose = () => {} }) => {
  const { control, register, handleSubmit, reset, watch } = useForm({
    defaultValues: previousBillingMonth(),
  });
  const dateFrom = watch('date_from');
  const dateTo = watch('date_to');
  const wholeMonth = isWholeBillingMonth(dateFrom, dateTo);

  useEffect(() => {
    if (visible) reset(previousBillingMonth());
  }, [visible, reset]);

  return (
    <AnimatedModal title="Billing report" visible={visible} onClose={onClose}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={cn('u-margin-top', styles.filtersModalForm)}>
        <InputGroup title="Date From">
          <Controller
            control={control}
            name="date_from"
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <DatePicker value={value} onChange={onChange} outputFormat="YYYY-MM-DD" />
            )}
          />
        </InputGroup>
        <InputGroup title="Date To">
          <Controller
            control={control}
            name="date_to"
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <DatePicker value={value} onChange={onChange} outputFormat="YYYY-MM-DD" />
            )}
          />
        </InputGroup>
        {wholeMonth && (
          <ToggleSwitch
            register={register}
            name="replace"
            label="Replace client copies"
            tooltip="Uploads this company's billing files and removes the older copies for this month."
          />
        )}
        <Button submit className="u-width-100 u-margin-top--large">
          Download
        </Button>
      </form>
    </AnimatedModal>
  );
};
